/**
 * Density Configuration — single source of truth for all density-dependent values.
 *
 * Every component that varies by density should read from here rather than
 * hard-coding magic numbers.
 */

import { TWOUP } from 'src/constants';
import type { BreakpointName } from 'src/helpers/widthToBreakpoint';
import type { DensityType } from 'src/types/store.types';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface DensityConfig {
  /** Grid spacing between cards (MUI spacing units) */
  cardMargin: number;
  /** CSS padding for the card content area below the thumbnail */
  cardContentPadding: string;
  /** Font size for the card title */
  titleFontSize: string;
  /** Bottom margin for the title (MUI spacing units) */
  titleMarginBottom: number;
  /** Font size for metadata field labels */
  fieldLabelFontSize: string;
  /** Font size for metadata field values */
  fieldValueFontSize: string;
  /** CSS padding for each metadata field row */
  fieldRowPadding: string;
  /** CSS margin for each metadata field row */
  fieldRowMargin: string;
  /** Minimum height for the thumbnail area (px) */
  thumbnailMinHeight: number;
  /** Maximum number of visible metadata fields (undefined = show all) */
  maxVisibleFields: number | undefined;
  /** Number of skeleton field placeholders to show while loading */
  skeletonFieldCount: number;
}

export interface MediaCardBreakPoints {
  xs: number;
  sm: number;
  md: number;
  lg: number;
  xl: number;
}

// ---------------------------------------------------------------------------
// Config map
// ---------------------------------------------------------------------------

export const DENSITY_CONFIGS: Record<DensityType, DensityConfig> = {
  compact: {
    cardMargin: 1,
    cardContentPadding: '4px 8px 0 8px',
    titleFontSize: '0.82rem',
    titleMarginBottom: 0.25,
    fieldLabelFontSize: '0.68rem',
    fieldValueFontSize: '0.72rem',
    fieldRowPadding: '0px 0',
    fieldRowMargin: '0 0 1px 0',
    thumbnailMinHeight: 80,
    maxVisibleFields: 1,
    skeletonFieldCount: 1,
  },
  comfortable: {
    cardMargin: 2,
    cardContentPadding: '8px 12px 0 12px',
    titleFontSize: '0.88rem',
    titleMarginBottom: 0.5,
    fieldLabelFontSize: '0.72rem',
    fieldValueFontSize: '0.78rem',
    fieldRowPadding: '1px 0',
    fieldRowMargin: '0 0 2px 0',
    thumbnailMinHeight: 122,
    maxVisibleFields: undefined,
    skeletonFieldCount: 3,
  },
  spacious: {
    cardMargin: 3,
    cardContentPadding: '12px 16px 0 16px',
    titleFontSize: '0.95rem',
    titleMarginBottom: 0.75,
    fieldLabelFontSize: '0.76rem',
    fieldValueFontSize: '0.84rem',
    fieldRowPadding: '2px 0',
    fieldRowMargin: '0 0 3px 0',
    thumbnailMinHeight: 160,
    maxVisibleFields: undefined,
    skeletonFieldCount: 3,
  },
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Returns the full density config for a given density level.
 */
export const getDensityConfig = (density: DensityType): DensityConfig => DENSITY_CONFIGS[density];

// ---------------------------------------------------------------------------
// Breakpoint resolver
// ---------------------------------------------------------------------------

/** Default breakpoints (no sidebar) per density */
const DEFAULT_BREAKPOINTS: Record<DensityType, MediaCardBreakPoints> = {
  compact: { xs: 12, sm: 6, md: 3, lg: 2, xl: 2 },
  comfortable: { xs: 12, sm: 6, md: 4, lg: 3, xl: 2 },
  spacious: { xs: 12, sm: 6, md: 4, lg: 4, xl: 3 },
};

/** Breakpoints when a sidebar is open (effective breakpoint >= md) */
const SIDEBAR_BREAKPOINTS: Record<DensityType, MediaCardBreakPoints> = {
  compact: { xs: 12, sm: 6, md: 4, lg: 3, xl: 2 },
  comfortable: { xs: 12, sm: 6, md: 6, lg: 4, xl: 3 },
  spacious: { xs: 12, sm: 6, md: 6, lg: 4, xl: 4 },
};

/** Two-up layout breakpoints (same for all densities in Phase 1) */
const TWOUP_BREAKPOINTS: MediaCardBreakPoints = { xs: 12, sm: 12, md: 6, lg: 6, xl: 6 };

/** All-12 fallback for very small viewports with sidebar */
const ALL_12: MediaCardBreakPoints = { xs: 12, sm: 12, md: 12, lg: 12, xl: 12 };

/** Small sidebar fallback */
const SIDEBAR_SM: MediaCardBreakPoints = { xs: 12, sm: 6, md: 6, lg: 6, xl: 6 };

/**
 * Returns the MUI Grid breakpoint values for a media card given the current
 * density, layout, effective breakpoint, and sidebar state.
 */
export const getMediaCardBreakpoints = (
  density: DensityType,
  layout: string,
  effectiveBreakpoint: BreakpointName | undefined,
  hasSidebar: boolean,
): MediaCardBreakPoints => {
  // Two-up is always the same regardless of density
  if (layout === TWOUP) return TWOUP_BREAKPOINTS;

  if (hasSidebar) {
    if (effectiveBreakpoint === 'xs') return ALL_12;
    if (effectiveBreakpoint === 'sm') return SIDEBAR_SM;
    return SIDEBAR_BREAKPOINTS[density];
  }

  return DEFAULT_BREAKPOINTS[density];
};
